import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { ProcessingService } from '../../../Processing/processing.service.upgrade';
import { SearchService } from '../../search.service';
import { QuickCriteria, QuickCriteriaFilter, QuickResultType } from './quickCriteria.model';
import { Constants } from 'src/app/constants.new';

@Component({
    selector: 'quick-search-criteria',
    templateUrl: './quickSearchCriteria.component.html',
    styleUrls: ['./quickSearchCriteria.component.scss'],
    standalone: false,
})
export class QuickSearchCriteriaComponent implements OnInit, OnChanges {
    constructor(
        private readonly _processingService: ProcessingService,
        private readonly _searchService: SearchService,
        private readonly _constants: Constants,
    ) {}

    @Input() criteria: QuickCriteria;
    @Input() isDocumentProcessing: boolean;
    @Input() resultTypes: QuickResultType[];
    @Input() currentDocument: any;
    @Input() isOpen: boolean;

    @Output() criteriaChange: EventEmitter<QuickCriteria> = new EventEmitter();
    @Output() search: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('dropdown') bsDropdown: BsDropdownDirective;

    @HostListener('keydown.enter') onEnterPressed() {
        this.search.emit(true);
    }

    states = [];
    propertyTypes = [];

    private _originalCriteria: QuickCriteria;
    private _addedAdditionalCriteria: boolean;
    private _criteriaUpdated: boolean;
    private _filterUpdated: boolean;

    ngOnInit(): void {
        const propertyTypes = this._constants.propertyTypeArr();
        propertyTypes.unshift({
            propertyTypeID: null,
            propTypeAbbr: '',
        });

        this.propertyTypes = propertyTypes;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentDocument && changes.currentDocument.currentValue) {
            this.resultTypes = this._processingService.getResultTypesByDocumentType(
                this.currentDocument.attachmentType,
            );

            if (this.resultTypes.length) {
                const foundResultType = this.resultTypes.find(
                    (x) => x.id === this.currentDocument.attachmentType.defaultResultsEntityTypeID,
                );

                this.criteria.resultType = foundResultType ? foundResultType.type : this.resultTypes[0].type;
            }

            this.criteria.activeFilters = this.criteria.activeFilters.map((filter) => {
                filter.text =
                    this.currentDocument[filter.field] || this.currentDocument[filter.resultField] || filter.text;

                return filter;
            });

            this.criteria.inactiveFilters.forEach((filter) => {
                filter.text =
                    this.currentDocument[filter.field] || this.currentDocument[filter.resultField] || filter.text;

                if (filter.text) {
                    this.criteria.activeFilters.push(filter);
                    this.criteria.inactiveFilters = this.criteria.inactiveFilters.filter((x) => x !== filter);
                }
            });

            this._originalCriteria = structuredClone(this.criteria);
        }
        if (changes.isOpen && !changes.isOpen.firstChange && !changes.isOpen.currentValue) {
            this.bsDropdown.hide();
        }
    }

    submitSearch(): void {
        this.search.emit();
    }

    addFilterButtonClicked(): void {
        this._addedAdditionalCriteria = true;
    }

    addFilter(filter: QuickCriteriaFilter): boolean {
        filter.text = '';

        this.criteria.inactiveFilters = this.criteria.inactiveFilters.filter((x) => x !== filter);
        this.criteria.activeFilters = [...this.criteria.activeFilters, filter];

        const sortedFilters = this.criteria.activeFilters.sort((a, b) => a.sequenceNum - b.sequenceNum);
        const sortedIndex = sortedFilters.findIndex((x) => x.field === filter.field);

        this._searchService.focusOnFilter(sortedIndex);

        return false;
    }

    removeFilter(filter: QuickCriteriaFilter, index: number): void {
        this.criteria.activeFilters = this.criteria.activeFilters.filter((x) => x !== filter);
        this.criteria.inactiveFilters = [...this.criteria.inactiveFilters, filter];

        this._searchService.focusOnFilter(index - 1);
    }

    changeResultType(): void {
        this._filterUpdated = true;

        const entityTypes = ['parcel', 'site', 'company'];
        const subEntityTypes = ['filing', 'taxBill', 'assessment', 'appeal'];

        const filterTypes = this.criteria.activeFilters.filter((x) => x.text).map((x) => x.associatedWith);

        const foundType = [...entityTypes, ...subEntityTypes].find((type) => {
            return filterTypes.includes(type) && this.resultTypes.some((x) => x.type === type);
        });

        if (!foundType || (foundType === 'parcel' && subEntityTypes.includes(this.criteria.resultType))) {
            return;
        }

        this.criteria.resultType = foundType;
    }

    showAnticipated(): boolean {
        const anticipatedResultTypes = ['assessment', 'appeal', 'taxBill', 'refund', 'filing'];

        return this.isDocumentProcessing && anticipatedResultTypes.includes(this.criteria.resultType);
    }

    searchCriteriaUpdated(filter: QuickCriteriaFilter): void {
        if (this.isDocumentProcessing && this.criteria.isIdpSearch && this._filterUpdated) {
            this._criteriaUpdated = true;

            if (filter.idpResults?.length) {
                const params = this.criteria.activeFilters
                    .filter((x) => x.text && x.idpResults?.length)
                    .map((x) => x.name);
                this._filterUpdated = false;
            }
        }
        this.criteriaChange.emit(this.criteria);
    }
}
